import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, Hidden } from "@mui/material";
import Modal from "../Modal";
import GetDirectionsButton from "../Buttons/GetDirectionsButton";
import { GoogleMap } from "../GoogleMap/GoogleMap";
import CourseTypeIcon from "../Icons/CourseTypeIcon";
import { useTranslation } from "react-i18next";
import CourseActionButton from "../Buttons/CourseActionButton";
import UserAssignmentContext from "../../context/UserAssignmentContext";

const InPersonAssignmentModalActions = ({
  trainingLocation,
  enrolledVersion
}) => {
  const { t } = useTranslation();
  const { onStartAssignment, onResumeAssignment, isStarted } = useContext(
    UserAssignmentContext
  );
  return (
    <Box display="flex" width="42%" justifyContent="space-between">
      {!!enrolledVersion && (
        <CourseActionButton
          isEnrolled={true}
          isStarted={isStarted(enrolledVersion.userAssignment)}
          courseType={enrolledVersion.courseType}
          onEnrollClick={() =>
            onStartAssignment(enrolledVersion.userAssignment)
          }
          onResumeCourseClick={() =>
            onResumeAssignment(enrolledVersion.userAssignment)
          }
          prereqs={enrolledVersion.prerequisites || []}
          startDate={enrolledVersion.zonedStartDate}
          endDate={enrolledVersion.zonedEndDate}
          iltClassType={enrolledVersion.typeName}
        />
      )}
      <GetDirectionsButton location={trainingLocation} />
      {!enrolledVersion && (
        <Button color="primary" variant="contained">
          {t("enroll")}
        </Button>
      )}
    </Box>
  );
};
const InPersonAssignmentModalContent = ({ assignment }) => {
  const {
    name,
    description,
    iltInfo: { instructor, trainingLocation: location }
  } = assignment;
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      flex={1}
      alignItems="flex-start"
      flexDirection={{ xs: "column", sm: "row" }}
    >
      <Box
        alignSelf={{ xs: "center", sm: "flex-start" }}
        mt={{ xs: 5, sm: 0 }}
        mb={{ xs: 4, sm: 0 }}
      >
        <img src={assignment.image} width="154px" height="154px" alt={name} />
      </Box>
      <Hidden smDown>
        <Box mx={2}>
          <CourseTypeIcon type={assignment.typeName} />
        </Box>
      </Hidden>
      <Box display="flex" flexDirection="column" flex={2}>
        <Box display="flex" flexDirection="row">
          <Hidden smUp>
            <Box mr={2}>
              <CourseTypeIcon type={assignment.typeName} />
            </Box>
          </Hidden>
          <Typography variant="h4">{name}</Typography>
        </Box>
        <Box mt={2} mb={3}>
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        </Box>
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }}>
          <Box display="flex" flexDirection="column" flex={3}>
            {!!instructor && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" color="textPrimary">
                  {t("instructor")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {instructor.name}
                </Typography>
              </Box>
            )}
            <Box mb={2.5} display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" color="textPrimary">
                  {t("startDate")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {assignment.scheduledDate}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textPrimary">
                  {t("startTime")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {assignment.scheduledTime}
                </Typography>
              </Box>
            </Box>

            <Box mb={2.5} display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" color="textPrimary">
                  {t("endDate")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {assignment.endDate}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textPrimary">
                  {t("endTime")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {assignment.endTime}
                </Typography>
              </Box>
            </Box>
            {!!location && (
              <>
                <Box mb={2.5}>
                  <Typography variant="subtitle2" color="textPrimary">
                    {t("location")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {location.name}
                  </Typography>
                </Box>

                <Box mb={2.5}>
                  <Typography variant="subtitle2" color="textPrimary">
                    {t("address")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {location.address1}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {location.address2}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >{`${location.city}, ${location.state} ${location.zip}`}</Typography>
                </Box>
              </>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            flex={4}
            position="relative"
            minHeight={268}
            width="100%"
            ml={{ xs: 0, sm: 4 }}
          >
            <GoogleMap
              lng={location.lng}
              lat={location.lat}
              locationName={location.name}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
const InPersonAssignmentModal = ({ assignment, open, onClose }) => {
  return (
    <Modal
      showCloseIconWithoutTitle
      open={open}
      onClose={onClose}
      dialogContent={<InPersonAssignmentModalContent assignment={assignment} />}
      dialogActions={
        <InPersonAssignmentModalActions
          trainingLocation={assignment.iltInfo.trainingLocation}
          enrolledVersion={assignment}
        />
      }
      maxWidth="md"
      fullWidth
      scrollable
    />
  );
};
export default InPersonAssignmentModal;

InPersonAssignmentModal.propTypes = {
  instructor: PropTypes.object,
  location: PropTypes.shape({
    name: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  scheduledDate: PropTypes.string,
  endDate: PropTypes.string,
  scheduledTime: PropTypes.string,
  endTime: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func
};

InPersonAssignmentModal.defaultProps = {
  open: false
};
